package com.trevorwiebe.timesheet.loads.presentation.admin

import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateType
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveDefaults
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.isCompleted
import com.trevorwiebe.timesheet.loads.presentation.LoadsViewModel
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import com.trevorwiebe.timesheet.mileage.presentation.LocationSearchDialog
import kotlinx.coroutines.flow.first
import org.koin.compose.viewmodel.koinViewModel
import com.trevorwiebe.timesheet.loads.presentation.DocumentViewer
import com.trevorwiebe.timesheet.mileage.presentation.rememberImagePickerLauncher

@Composable
expect fun AddLoadDialog(state: LoadsState, onEvent: (LoadsEvent) -> Unit)

/**
 * The Edit Load dialog for [load], opened from outside the Loads screens (e.g. to price a load
 * while invoicing). Its [LoadsViewModel] is created on first use, then reused by the screen.
 */
@Composable
fun EditLoadDialog(load: Load, onDismiss: () -> Unit, viewModel: LoadsViewModel = koinViewModel()) {
    val state by viewModel.state.collectAsState()
    LaunchedEffect(load.id) {
        viewModel.onEvent(LoadsEvent.OnEditLoad(load))
        viewModel.state.first { !it.showAddDialog }
        onDismiss()
    }
    if (state.showAddDialog) AddLoadDialog(state = state, onEvent = viewModel::onEvent)
}

@Composable
internal fun AddLoadFormContent(state: LoadsState, onEvent: (LoadsEvent) -> Unit) {
    val form = state.addLoadForm

    fun update(newForm: AddLoadFormState) = onEvent(LoadsEvent.OnUpdateForm(newForm))

    fun updatePickup(index: Int, transform: (LoadStopEntry) -> LoadStopEntry) =
        update(form.copy(pickups = form.pickups.mapIndexed { i, e -> if (i == index) transform(e) else e }))

    fun updateDrop(index: Int, transform: (LoadStopEntry) -> LoadStopEntry) =
        update(form.copy(drops = form.drops.mapIndexed { i, e -> if (i == index) transform(e) else e }))

    val focusManager = LocalFocusManager.current
    var showCustomerPicker by remember { mutableStateOf(false) }
    var showPickupDatePicker by remember { mutableStateOf(false) }
    var showDropDatePicker by remember { mutableStateOf(false) }

    // The form's sections, split so wide windows can lay them out side by side.
    val leftSections: @Composable () -> Unit = {
        // ── Customer ────────────────────────────────────────────────────────
        SectionLabel("Customer")
        PickerRow(
            label = customerLabel(form, state.customers).ifEmpty { "Select a customer" },
            isPlaceholder = !form.hasCustomer,
            onClick = { focusManager.clearFocus(); showCustomerPicker = true }
        )
        form.customerError?.let { FieldError(it) }

        // ── Pickups ─────────────────────────────────────────────────────────
        Spacer(Modifier.height(24.dp))
        form.pickups.forEachIndexed { index, entry ->
            StopEntryRow(
                title = if (form.pickups.size > 1) "Pickup ${index + 1}" else "Pickup",
                entry = entry,
                locations = state.locations,
                canRemove = form.pickups.size > 1,
                onRemove = { onEvent(LoadsEvent.OnRemovePickup(index)) },
                onSelectExisting = { location ->
                    updatePickup(index) {
                        it.copy(locationId = location.id, isNewLocation = false, locationError = null)
                    }
                },
                onNewLocation = { name, city, st ->
                    updatePickup(index) {
                        it.copy(
                            isNewLocation = true,
                            locationId = "",
                            newLocationName = name,
                            newLocationCity = city,
                            newLocationState = st,
                            locationError = null,
                        )
                    }
                },
            )
        }
        AddStopButton(text = "Add pickup", onClick = { onEvent(LoadsEvent.OnAddPickup) })

        // ── Drops ───────────────────────────────────────────────────────────
        Spacer(Modifier.height(24.dp))
        form.drops.forEachIndexed { index, entry ->
            StopEntryRow(
                title = if (form.drops.size > 1) "Drop ${index + 1}" else "Drop",
                entry = entry,
                locations = state.locations,
                canRemove = form.drops.size > 1,
                onRemove = { onEvent(LoadsEvent.OnRemoveDrop(index)) },
                onSelectExisting = { location ->
                    updateDrop(index) {
                        it.copy(locationId = location.id, isNewLocation = false, locationError = null)
                    }
                },
                onNewLocation = { name, city, st ->
                    updateDrop(index) {
                        it.copy(
                            isNewLocation = true,
                            locationId = "",
                            newLocationName = name,
                            newLocationCity = city,
                            newLocationState = st,
                            locationError = null,
                        )
                    }
                },
            )
        }
        AddStopButton(text = "Add drop", onClick = { onEvent(LoadsEvent.OnAddDrop) })

        // ── Pickup date ─────────────────────────────────────────────────────
        SectionLabel("Pickup date")
        DateRow(
            label = "Date",
            date = Util.toFriendlyDate(form.pickupDate),
            onChangeClick = { focusManager.clearFocus(); showPickupDatePicker = true }
        )

        // ── Drop date ───────────────────────────────────────────────────────
        SectionLabel("Drop date")
        DateRow(
            label = "Date",
            date = Util.toFriendlyDate(form.originalDate),
            onChangeClick = { focusManager.clearFocus(); showDropDatePicker = true }
        )

        // ── Status ──────────────────────────────────────────────────────────
        SectionLabel("Status")
        Row(verticalAlignment = Alignment.CenterVertically) {
            StatusCheckbox(
                label = "Set up",
                checked = form.setUp,
                onCheckedChange = { update(form.copy(setUp = it)) },
            )
            Spacer(Modifier.weight(1f))
            StatusCheckbox(
                label = "Ready",
                checked = form.ready,
                onCheckedChange = { update(form.copy(ready = it)) },
            )
            Spacer(Modifier.weight(1f))
        }
    }

    val pickFile = rememberLoadFilePickerLauncher { name, bytes ->
        onEvent(LoadsEvent.OnAttachmentSelected(name, bytes))
    }
    var openDocumentIndex by remember { mutableStateOf<Int?>(null) }
    val scanDocument = rememberImagePickerLauncher { pages ->
        onEvent(LoadsEvent.OnScanDocument(pages))
    }

    val rightSections: @Composable () -> Unit = {
        // ── Rate (admins + dispatchers) ─────────────────────────────────────
        // Pricing is never shown to drivers; driver edits still carry the form's rate
        // through untouched, so an existing rate survives their save.
        if (state.isElevated) {
            SectionLabel("Rate")
            LoadRatePickerRow(
                loadRates = state.loadRates,
                suggested = state.suggestedLoadRates,
                locations = state.locations,
                rate = form.rate,
                onSelect = { focusManager.clearFocus(); onEvent(LoadsEvent.OnSelectLoadRate(it)) },
            )
            form.rate?.let { rate ->
                Spacer(Modifier.height(8.dp))
                when (rate.rateType) {
                    LoadRateType.FLAT_RATE -> {
                        LoadTextField(
                            text = rate.flatRateValue,
                            placeholder = "Flat rate amount ($)",
                            keyboardType = KeyboardType.Decimal,
                            onTextChange = { update(form.copy(rate = rate.copy(flatRateValue = it))) }
                        )
                    }
                    LoadRateType.PER_UNIT ->
                        PerUnitQuantityFields(rate) { update(form.copy(rate = it)) }
                }
                Spacer(Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    ChargeField(
                        label = "Fuel %",
                        value = rate.fuelSurchargePercent,
                        onTextChange = { update(form.copy(rate = rate.copy(fuelSurchargePercent = it))) },
                    )
                    ChargeField(
                        label = "Detention",
                        value = rate.detention,
                        onTextChange = { update(form.copy(rate = rate.copy(detention = it))) },
                    )
                    ChargeField(
                        label = "Stop-off",
                        value = rate.stopOffCharge,
                        onTextChange = { update(form.copy(rate = rate.copy(stopOffCharge = it))) },
                    )
                    ChargeField(
                        label = "Other",
                        value = rate.otherCharge,
                        imeAction = ImeAction.Done,
                        onTextChange = { update(form.copy(rate = rate.copy(otherCharge = it))) },
                    )
                }
                Spacer(Modifier.height(12.dp))
                RateTotalsRow(rate)
            }
        }

        // ── Driver ──────────────────────────────────────────────────────────
        // Optional in general; required for after-the-fact (past-dated) loads entered post-run.
        val driverRequired = form.isPast(state.today)
        SectionLabel(if (driverRequired) "Driver (required)" else "Driver")
        DriverPickerRow(
            users = state.users,
            selectedId = form.employeeId,
            onSelect = { update(form.copy(employeeId = it, driverError = null)) },
        )
        form.driverError?.let { FieldError(it) }

        // ── Truck ───────────────────────────────────────────────────────────
        if (state.isElevated) {
            SectionLabel("Truck")
            TruckPickerRow(
                trucks = state.trucks,
                selectedId = form.truckId,
                onSelect = { update(form.copy(truckId = it)) },
            )
        }

        // ── Details ─────────────────────────────────────────────────────────
        SectionLabel("Details")
        LoadTextField(
            text = form.loadNumber,
            placeholder = "Load number",
            onTextChange = { update(form.copy(loadNumber = it)) }
        )
        Spacer(Modifier.height(8.dp))
        LoadTextField(
            text = form.po,
            placeholder = "PO",
            onTextChange = { update(form.copy(po = it)) }
        )
        Spacer(Modifier.height(8.dp))
        LoadTextField(
            text = form.orderNumber,
            placeholder = "Order number",
            onTextChange = { update(form.copy(orderNumber = it)) }
        )
        Spacer(Modifier.height(8.dp))
        LoadTextField(
            text = form.commodity,
            placeholder = "Commodity",
            capitalization = KeyboardCapitalization.Sentences,
            onTextChange = { update(form.copy(commodity = it)) }
        )
        Spacer(Modifier.height(8.dp))
        LoadTextField(
            text = form.customerRequirements,
            placeholder = "Customer requirements",
            capitalization = KeyboardCapitalization.Sentences,
            imeAction = ImeAction.Done,
            onTextChange = { update(form.copy(customerRequirements = it)) }
        )

        // ── Documents ───────────────────────────────────────────────────────
        SectionLabel("Documents")
        form.attachments.forEachIndexed { index, attachment ->
            val uploader = state.users.find { it.id == attachment.uploadedById }?.displayName
            AttachmentRow(
                name = if (uploader != null) "${attachment.name} · $uploader" else attachment.name,
                onOpen = {
                    onEvent(LoadsEvent.OnOpenDocument(attachment.url))
                    openDocumentIndex = index
                },
                onRemove = { onEvent(LoadsEvent.OnRemoveAttachment(index)) },
            )
        }
        form.attachmentError?.let { FieldError(it) }
        if (form.isUploadingAttachment) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.padding(vertical = 8.dp),
            ) {
                CircularProgressIndicator(
                    modifier = Modifier.height(20.dp).width(20.dp),
                    strokeWidth = 2.dp,
                    color = MaterialTheme.colorScheme.tertiary,
                )
                Text("Uploading…", modifier = Modifier.padding(start = 8.dp))
            }
        } else {
            Row {
                AddStopButton(
                    text = "Add file",
                    onClick = { focusManager.clearFocus(); pickFile() },
                )
                AddStopButton(
                    text = "Scan document",
                    onClick = { focusManager.clearFocus(); scanDocument() },
                )
            }
        }
    }

    BoxWithConstraints(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.TopCenter,
    ) {
        val twoColumn = maxWidth >= 760.dp
        Column(
            modifier = Modifier
                .widthIn(max = if (twoColumn) 1040.dp else ResponsiveDefaults.dialogMaxWidth)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 16.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = if (state.isEditing) "Edit Load" else "Add Load",
                    style = MaterialTheme.typography.titleLarge,
                    modifier = Modifier.weight(1f)
                )
                val editingLoad = state.editingLoad
                if (editingLoad != null && editingLoad.isCompleted(state.today)) {
                    TextButton(
                        onClick = {
                            focusManager.clearFocus()
                            onEvent(LoadsEvent.OnUncompleteLoad(editingLoad))
                        },
                        colors = ButtonDefaults.buttonColors(
                            contentColor = MaterialTheme.colorScheme.tertiary,
                            containerColor = MaterialTheme.colorScheme.background
                        )
                    ) {
                        Text("Unschedule")
                    }
                }
                if (state.isEditing) {
                    TextButton(
                        onClick = { focusManager.clearFocus(); onEvent(LoadsEvent.OnShowDuplicateDialog) },
                        colors = ButtonDefaults.buttonColors(
                            contentColor = MaterialTheme.colorScheme.tertiary,
                            containerColor = MaterialTheme.colorScheme.background
                        )
                    ) {
                        Text("Duplicate")
                    }
                }
            }

            if (twoColumn) {
                Row(horizontalArrangement = Arrangement.spacedBy(32.dp)) {
                    Column(Modifier.weight(1f)) { leftSections() }
                    Column(Modifier.weight(1f)) { rightSections() }
                }
            } else {
                leftSections()
                rightSections()
            }

            Spacer(Modifier.height(16.dp))
            DialogButtonRow(
                confirmButtonText = if (state.isSaving) "Saving…" else "Save",
                onConfirmClick = { focusManager.clearFocus(); onEvent(LoadsEvent.OnSaveLoad) },
                confirmEnabled = !state.isSaving,
                dismissButtonText = "Cancel",
                onDismissClick = { onEvent(LoadsEvent.OnDismissAddDialog) },
                // Editing an existing load exposes a destructive delete, gated behind a confirm dialog.
                neutralButtonText = if (state.isEditing) "Delete" else null,
                onNeutralClick = { focusManager.clearFocus(); onEvent(LoadsEvent.OnRequestDelete) }
            )
            Spacer(Modifier.height(8.dp))
        }
    }

    if (showCustomerPicker) {
        CustomerSearchDialog(
            customers = state.customers,
            initialNewCustomer = if (form.isNewCustomer) Customer(
                id = "",
                contactName = form.newCustomerContactName,
                companyName = form.newCustomerCompanyName,
                address = form.newCustomerAddress,
                phone = form.newCustomerPhone,
                emails = listOf(form.newCustomerEmail),
                paymentTerms = form.newCustomerPaymentTerms,
                notes = form.newCustomerNotes,
            ) else null,
            onSelect = { customer ->
                update(form.copy(customerId = customer.id, isNewCustomer = false, customerError = null))
                showCustomerPicker = false
            },
            onNewCustomer = { c ->
                update(
                    form.copy(
                        isNewCustomer = true,
                        customerId = "",
                        newCustomerContactName = c.contactName,
                        newCustomerCompanyName = c.companyName,
                        newCustomerAddress = c.address,
                        newCustomerPhone = c.phone,
                        newCustomerEmail = c.emails.firstOrNull().orEmpty(),
                        newCustomerPaymentTerms = c.paymentTerms,
                        newCustomerNotes = c.notes,
                        customerError = null
                    )
                )
                showCustomerPicker = false
            },
            onDismiss = { showCustomerPicker = false }
        )
    }

    if (state.showDuplicateDialog) {
        DuplicateLoadDialog(state = state, onEvent = onEvent)
    }

    val openIndex = openDocumentIndex
    val openDocument = openIndex?.let { form.attachments.getOrNull(it) }
    if (openIndex != null && openDocument != null) {
        DocumentViewer(
            document = openDocument,
            pdf = state.documentPdfs[openDocument.url],
            isSaving = form.isUploadingAttachment,
            onDelete = {
                onEvent(LoadsEvent.OnRemoveAttachment(openIndex))
                openDocumentIndex = null
            },
            onDismiss = { openDocumentIndex = null },
        )
    }

    NativeDialog(
        visible = state.showDeleteConfirm,
        title = "Delete Load",
        message = "Delete this load? This also removes all of its pickup and drop stops. This action cannot be undone.",
        confirmText = "Delete",
        onConfirm = { onEvent(LoadsEvent.OnConfirmDelete) },
        dismissText = "Cancel",
        onDismiss = { onEvent(LoadsEvent.OnDismissDelete) }
    )

    // Pickup-date picker — moves the drop date along with it.
    if (showPickupDatePicker) {
        NativeDatePicker(
            date = form.pickupDate,
            showDatePicker = true,
            onDismiss = { showPickupDatePicker = false },
            onDateSelected = { picked ->
                update(form.withPickupDate(picked))
                showPickupDatePicker = false
            }
        )
    }

    // Drop-date picker — independent of the pickup date.
    if (showDropDatePicker) {
        NativeDatePicker(
            date = form.originalDate,
            showDatePicker = true,
            onDismiss = { showDropDatePicker = false },
            onDateSelected = { picked ->
                update(form.copy(originalDate = picked))
                showDropDatePicker = false
            }
        )
    }
}

/**
 * Small dialog stacked on the edit dialog that duplicates the load being edited: the user sets only
 * the new dates and load/PO/order numbers; everything else copies from the source load on save.
 */
@Composable
private fun DuplicateLoadDialog(state: LoadsState, onEvent: (LoadsEvent) -> Unit) {
    val form = state.duplicateForm

    fun update(newForm: DuplicateLoadFormState) = onEvent(LoadsEvent.OnUpdateDuplicateForm(newForm))

    var showPickupDatePicker by remember { mutableStateOf(false) }
    var showDropDatePicker by remember { mutableStateOf(false) }

    Dialog(onDismissRequest = { onEvent(LoadsEvent.OnDismissDuplicateDialog) }) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp),
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp)
            ) {
                Text(
                    text = "Duplicate Load",
                    style = MaterialTheme.typography.titleLarge
                )

                SectionLabel("Pickup date")
                DateRow(
                    label = "Date",
                    date = Util.toFriendlyDate(form.pickupDate),
                    onChangeClick = { showPickupDatePicker = true }
                )

                SectionLabel("Drop date")
                DateRow(
                    label = "Date",
                    date = Util.toFriendlyDate(form.originalDate),
                    onChangeClick = { showDropDatePicker = true }
                )

                SectionLabel("Details")
                LoadTextField(
                    text = form.loadNumber,
                    placeholder = "Load number",
                    onTextChange = { update(form.copy(loadNumber = it)) }
                )
                Spacer(Modifier.height(8.dp))
                LoadTextField(
                    text = form.po,
                    placeholder = "PO",
                    onTextChange = { update(form.copy(po = it)) }
                )
                Spacer(Modifier.height(8.dp))
                LoadTextField(
                    text = form.orderNumber,
                    placeholder = "Order number",
                    imeAction = ImeAction.Done,
                    onTextChange = { update(form.copy(orderNumber = it)) }
                )

                // ── Rate ────────────────────────────────────────────────────
                // Prefilled from the source load (template fixed here); the user keeps
                // or edits the value and charges.
                if (state.isElevated) {
                    form.rate?.let { rate ->
                        SectionLabel("Rate")
                        val route = rate.routeLabel(state.locations)
                        Text(
                            text = if (route.isEmpty()) rate.snapshotLabel
                            else "$route — ${rate.snapshotLabel}",
                            style = MaterialTheme.typography.bodyLarge,
                        )
                        Spacer(Modifier.height(8.dp))
                        when (rate.rateType) {
                            LoadRateType.FLAT_RATE -> LoadTextField(
                                text = rate.flatRateValue,
                                placeholder = "Flat rate amount ($)",
                                keyboardType = KeyboardType.Decimal,
                                onTextChange = { update(form.copy(rate = rate.copy(flatRateValue = it))) }
                            )
                            LoadRateType.PER_UNIT ->
                                PerUnitQuantityFields(rate) { update(form.copy(rate = it)) }
                        }
                        Spacer(Modifier.height(8.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            ChargeField(
                                label = "Fuel %",
                                value = rate.fuelSurchargePercent,
                                onTextChange = { update(form.copy(rate = rate.copy(fuelSurchargePercent = it))) },
                            )
                            ChargeField(
                                label = "Detention",
                                value = rate.detention,
                                onTextChange = { update(form.copy(rate = rate.copy(detention = it))) },
                            )
                            ChargeField(
                                label = "Stop-off",
                                value = rate.stopOffCharge,
                                onTextChange = { update(form.copy(rate = rate.copy(stopOffCharge = it))) },
                            )
                            ChargeField(
                                label = "Other",
                                value = rate.otherCharge,
                                imeAction = ImeAction.Done,
                                onTextChange = { update(form.copy(rate = rate.copy(otherCharge = it))) },
                            )
                        }
                        Spacer(Modifier.height(12.dp))
                        RateTotalsRow(rate)
                    }
                }

                Spacer(Modifier.height(16.dp))
                DialogButtonRow(
                    confirmButtonText = if (state.isDuplicating) "Creating…" else "Create",
                    onConfirmClick = { onEvent(LoadsEvent.OnSaveDuplicate) },
                    confirmEnabled = !state.isDuplicating,
                    dismissButtonText = "Cancel",
                    onDismissClick = { onEvent(LoadsEvent.OnDismissDuplicateDialog) },
                )
            }
        }
    }

    // Pickup-date picker — moves the drop date along with it.
    if (showPickupDatePicker) {
        NativeDatePicker(
            date = form.pickupDate,
            showDatePicker = true,
            onDismiss = { showPickupDatePicker = false },
            onDateSelected = { picked ->
                update(form.withPickupDate(picked))
                showPickupDatePicker = false
            }
        )
    }

    // Drop-date picker — independent of the pickup date.
    if (showDropDatePicker) {
        NativeDatePicker(
            date = form.originalDate,
            showDatePicker = true,
            onDismiss = { showDropDatePicker = false },
            onDateSelected = { picked ->
                update(form.copy(originalDate = picked))
                showDropDatePicker = false
            }
        )
    }
}

/** One pickup/drop stop in the add-load form: a header (with optional remove), a location
 *  picker, and an inline error. */
@Composable
private fun StopEntryRow(
    title: String,
    entry: LoadStopEntry,
    locations: List<Location>,
    canRemove: Boolean,
    onRemove: () -> Unit,
    onSelectExisting: (Location) -> Unit,
    onNewLocation: (name: String, city: String, state: String) -> Unit,
) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
        Text(
            text = title,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.weight(1f)
        )
        if (canRemove) {
            IconButton(onClick = onRemove, modifier = Modifier.height(24.dp)) {
                Icon(
                    imageVector = Icons.Default.Delete,
                    contentDescription = "Remove $title",
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
    Spacer(Modifier.height(4.dp))
    LoadLocationPickerRow(
        locations = locations,
        isNew = entry.isNewLocation,
        selectedId = entry.locationId,
        newName = entry.newLocationName,
        newCity = entry.newLocationCity,
        newState = entry.newLocationState,
        onSelectExisting = onSelectExisting,
        onNewLocation = onNewLocation,
    )
    entry.locationError?.let { FieldError(it) }
}

/** One uploaded document: click the name to open it, the trash can to remove it from the load. */
@Composable
private fun AttachmentRow(name: String, onOpen: () -> Unit, onRemove: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = name,
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.tertiary,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier
                .weight(1f)
                .clip(RoundedCornerShape(4.dp))
                .clickable { onOpen() }
                .padding(vertical = 8.dp),
        )
        IconButton(onClick = onRemove) {
            Icon(
                imageVector = Icons.Default.Delete,
                contentDescription = "Remove $name",
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun AddStopButton(text: String, onClick: () -> Unit) {
    TextButton(
        onClick = onClick,
        colors = ButtonDefaults.buttonColors(
            contentColor = MaterialTheme.colorScheme.tertiary,
            containerColor = MaterialTheme.colorScheme.background
        )
    ) {
        Icon(Icons.Default.Add, contentDescription = null)
        Text(text, modifier = Modifier.padding(start = 4.dp))
    }
}

@Composable
private fun StatusCheckbox(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.clickable { onCheckedChange(!checked) },
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = CheckboxDefaults.colors(
                checkedColor = MaterialTheme.colorScheme.tertiary,
                checkmarkColor = MaterialTheme.colorScheme.onTertiary,
                uncheckedColor = MaterialTheme.colorScheme.tertiary,
            ),
        )
        Text(text = label, style = MaterialTheme.typography.bodyLarge)
    }
}

// ── Shared small pieces ─────────────────────────────────────────────────────

@Composable
private fun SectionLabel(text: String) {
    Spacer(Modifier.height(16.dp))
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
    Spacer(Modifier.height(4.dp))
}

@Composable
private fun FieldError(message: String) {
    Spacer(Modifier.height(4.dp))
    Text(
        text = message,
        color = MaterialTheme.colorScheme.error,
        style = MaterialTheme.typography.bodySmall
    )
}

@Composable
private fun LoadTextField(
    text: String,
    placeholder: String,
    capitalization: KeyboardCapitalization = KeyboardCapitalization.None,
    imeAction: ImeAction = ImeAction.Next,
    keyboardType: KeyboardType = KeyboardType.Text,
    onTextChange: (String) -> Unit,
) {
    TimesheetTextField(
        text = text,
        placeholder = placeholder,
        keyboardOptions = KeyboardOptions(
            capitalization = capitalization,
            imeAction = imeAction,
            keyboardType = keyboardType,
        ),
        onTextChange = onTextChange
    )
}

/** Read-only field that opens a picker; shows placeholder styling when nothing is selected. */
@Composable
private fun PickerRow(label: String, isPlaceholder: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(4.dp))
            .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
            .clickable { onClick() }
            .padding(horizontal = 16.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyLarge,
            color = if (isPlaceholder) MaterialTheme.colorScheme.onSurfaceVariant
            else MaterialTheme.colorScheme.onBackground,
            modifier = Modifier.weight(1f)
        )
        Icon(
            imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

/** Driver selector: a picker row that drops down the active users plus an "Unassigned" option. */
@Composable
private fun DriverPickerRow(
    users: List<DatabaseUserModel>,
    selectedId: String,
    onSelect: (String) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val label = users.firstOrNull { it.id == selectedId }?.displayName ?: "Unassigned"
    Box {
        PickerRow(
            label = label,
            isPlaceholder = selectedId.isEmpty(),
            onClick = { expanded = true },
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            DropdownMenuItem(
                text = { Text("Unassigned") },
                onClick = { onSelect(""); expanded = false },
            )
            users.forEach { user ->
                DropdownMenuItem(
                    text = { Text(user.displayName) },
                    onClick = { onSelect(user.id); expanded = false },
                )
            }
        }
    }
}

/** Truck override: "Auto" leaves the truck to be matched from the driver's mileage log. */
@Composable
private fun TruckPickerRow(
    trucks: List<Equipment>,
    selectedId: String,
    onSelect: (String) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val label = trucks.firstOrNull { it.id == selectedId }?.let { "Unit ${it.unitNumber}" }
        ?: "Auto (from mileage log)"
    Box {
        PickerRow(
            label = label,
            isPlaceholder = selectedId.isEmpty(),
            onClick = { expanded = true },
        )
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            DropdownMenuItem(
                text = { Text("Auto (from mileage log)") },
                onClick = { onSelect(""); expanded = false },
            )
            trucks.forEach { truck ->
                DropdownMenuItem(
                    text = { Text("Unit ${truck.unitNumber}") },
                    onClick = { onSelect(truck.id); expanded = false },
                )
            }
        }
    }
}

/** Weight-based units show paired lbs/quantity fields that fill each other in as the user types. */
@Composable
private fun PerUnitQuantityFields(
    rate: LoadRateFormEntry,
    onRateChange: (LoadRateFormEntry) -> Unit,
) {
    LoadTextField(
        text = rate.perUnitValue,
        placeholder = "Price per ${rate.unitLabel} ($)",
        keyboardType = KeyboardType.Decimal,
        onTextChange = { onRateChange(rate.copy(perUnitValue = it)) },
    )
    Spacer(Modifier.height(8.dp))
    if (rate.isWeightBased) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            ChargeField(
                label = "Weight (lbs)",
                value = rate.pounds,
                onTextChange = { onRateChange(rate.withPounds(it)) },
            )
            ChargeField(
                label = "Quantity (${rate.unitLabel}s)",
                value = rate.unitQuantity,
                onTextChange = { onRateChange(rate.withUnitQuantity(it)) },
            )
        }
    } else {
        LoadTextField(
            text = rate.unitQuantity,
            placeholder = "Quantity (${rate.unitLabel}s)",
            keyboardType = KeyboardType.Decimal,
            onTextChange = { onRateChange(rate.withUnitQuantity(it)) },
        )
    }
}

/** One of the four extra-charge fields, sized to sit four-across in a row on any width. */
@Composable
private fun RowScope.ChargeField(
    label: String,
    value: String,
    imeAction: ImeAction = ImeAction.Next,
    onTextChange: (String) -> Unit,
) {
    Column(modifier = Modifier.weight(1f)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
        )
        Spacer(Modifier.height(4.dp))
        TimesheetTextField(
            text = value,
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Decimal,
                imeAction = imeAction,
            ),
            onTextChange = onTextChange,
        )
    }
}

/**
 * Rate template selector: a picker row that opens the searchable rate dialog. Rates negotiated for
 * the form's current pickup → drop pair are listed first.
 */
@Composable
private fun LoadRatePickerRow(
    loadRates: List<LoadRate>,
    suggested: List<LoadRate>,
    locations: List<Location>,
    rate: LoadRateFormEntry?,
    onSelect: (LoadRate?) -> Unit,
) {
    var showPicker by remember { mutableStateOf(false) }

    // A missing template (deleted after this load was priced) falls back to a label
    // derived from the snapshot itself.
    val label = rate?.let { r ->
        val template = loadRates.firstOrNull { it.id == r.parentRateId }
        val route = template?.routeLabel(locations) ?: r.routeLabel(locations)
        val name = template?.label ?: r.snapshotLabel
        if (route.isEmpty()) name else "$route — $name"
    } ?: "None"

    PickerRow(
        label = label,
        isPlaceholder = rate == null,
        onClick = { showPicker = true },
    )

    if (showPicker) {
        LoadRateSearchDialog(
            loadRates = loadRates,
            suggested = suggested,
            locations = locations,
            onSelect = { onSelect(it); showPicker = false },
            onDismiss = { showPicker = false },
        )
    }
}

/** Live total with the computed fuel dollar amount alongside. */
@Composable
private fun RateTotalsRow(rate: LoadRateFormEntry) {
    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        Text(
            text = "Total: ${rate.totalFormatted}",
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.weight(1f),
        )
        Text(
            text = "Fuel: ${rate.fuelAmountFormatted}",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun DateRow(label: String, date: String, onChangeClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = "$label: $date",
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.weight(1f)
        )
        TextButton(
            onClick = onChangeClick,
            colors = ButtonDefaults.buttonColors(
                contentColor = MaterialTheme.colorScheme.tertiary,
                containerColor = MaterialTheme.colorScheme.background
            )
        ) {
            Text("Change")
        }
    }
}

private fun customerLabel(form: AddLoadFormState, customers: List<Customer>): String = when {
    form.isNewCustomer && form.newCustomerContactName.isNotEmpty() ->
        listOf(form.newCustomerCompanyName, form.newCustomerContactName).filter { it.isNotEmpty() }.joinToString(" — ")
    form.isNewCustomer -> "New customer…"
    else -> customers.firstOrNull { it.id == form.customerId }?.let { customerDisplayName(it) } ?: ""
}

internal fun customerDisplayName(customer: Customer): String =
    if (customer.companyName.isNotEmpty()) "${customer.companyName} — ${customer.contactName}"
    else customer.contactName

/** Location selector for the pickup/drop fields; reuses the mileage searchable picker. */
@Composable
private fun LoadLocationPickerRow(
    locations: List<Location>,
    isNew: Boolean,
    selectedId: String,
    newName: String,
    newCity: String,
    newState: String,
    onSelectExisting: (Location) -> Unit,
    onNewLocation: (name: String, city: String, state: String) -> Unit,
) {
    var showPicker by remember { mutableStateOf(false) }
    val label = when {
        isNew && newName.isNotEmpty() -> "$newName — $newCity, $newState"
        isNew -> "New location…"
        else -> locations.firstOrNull { it.id == selectedId }
            ?.let { "${it.name} — ${it.city}, ${it.state}" } ?: ""
    }

    PickerRow(
        label = label.ifEmpty { "Select a location" },
        isPlaceholder = label.isEmpty(),
        onClick = { showPicker = true }
    )

    if (showPicker) {
        LocationSearchDialog(
            locations = locations,
            nameFieldLabel = "Location name",
            initialName = newName,
            initialCity = newCity,
            initialState = newState,
            onSelect = { onSelectExisting(it); showPicker = false },
            onNewLocation = { name, city, st -> onNewLocation(name, city, st); showPicker = false },
            onDismiss = { showPicker = false }
        )
    }
}

// ── Customer search picker ───────────────────────────────────────────────────

@Composable
expect fun CustomerSearchDialog(
    customers: List<Customer>,
    initialNewCustomer: Customer?,
    onSelect: (Customer) -> Unit,
    onNewCustomer: (Customer) -> Unit,
    onDismiss: () -> Unit,
    // Select-only mode for filters, mirroring LocationSearchDialog: hides "New customer…" and
    // offers a clear row (e.g. "All Customers") that routes to [onClear].
    title: String = "Select Customer",
    allowNewCustomer: Boolean = true,
    clearLabel: String? = null,
    onClear: (() -> Unit)? = null,
)

@Composable
internal fun CustomerSearchContent(
    customers: List<Customer>,
    initialNewCustomer: Customer?,
    onSelect: (Customer) -> Unit,
    onNewCustomer: (Customer) -> Unit,
    onDismiss: () -> Unit,
    title: String = "Select Customer",
    allowNewCustomer: Boolean = true,
    clearLabel: String? = null,
    onClear: (() -> Unit)? = null,
) {
    var query by remember { mutableStateOf("") }
    var addingNew by remember { mutableStateOf(initialNewCustomer != null) }
    var contactName by remember { mutableStateOf(initialNewCustomer?.contactName ?: "") }
    var companyName by remember { mutableStateOf(initialNewCustomer?.companyName ?: "") }
    var address by remember { mutableStateOf(initialNewCustomer?.address ?: "") }
    var phone by remember { mutableStateOf(initialNewCustomer?.phone ?: "") }
    var email by remember { mutableStateOf(initialNewCustomer?.emails?.firstOrNull() ?: "") }
    var paymentTerms by remember { mutableStateOf(initialNewCustomer?.paymentTerms ?: "") }
    var notes by remember { mutableStateOf(initialNewCustomer?.notes ?: "") }
    var nameError by remember { mutableStateOf<String?>(null) }

    val focusManager = LocalFocusManager.current

    val filtered = if (query.isBlank()) {
        customers
    } else {
        customers.filter {
            it.contactName.contains(query, ignoreCase = true) ||
                it.companyName.contains(query, ignoreCase = true) ||
                it.phone.contains(query, ignoreCase = true)
        }
    }

    fun submitNew() {
        if (contactName.isBlank()) {
            nameError = "Enter a contact name."
            return
        }
        focusManager.clearFocus()
        onNewCustomer(
            Customer(
                id = "",
                contactName = contactName.trim(),
                companyName = companyName.trim(),
                address = address.trim(),
                phone = phone.trim(),
                emails = listOf(email.trim()).filter { it.isNotEmpty() },
                paymentTerms = paymentTerms.trim(),
                notes = notes.trim(),
            )
        )
    }

    Surface(
        modifier = Modifier.fillMaxSize(),
        color = MaterialTheme.colorScheme.background
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .statusBarsPadding()
                .navigationBarsPadding()
                .imePadding()
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp, bottom = 8.dp, end = 16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Close")
                }
                Text(text = title, style = MaterialTheme.typography.titleLarge)
            }
            Box(modifier = Modifier.padding(horizontal = 16.dp)) {
                TimesheetTextField(
                    text = query,
                    placeholder = "Search by name, company, or phone",
                    onTextChange = { query = it }
                )
            }
            Spacer(Modifier.height(8.dp))
            LazyColumn(modifier = Modifier.fillMaxWidth().weight(1f)) {
                if (clearLabel != null && onClear != null) {
                    item {
                        Text(
                            text = clearLabel,
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.tertiary,
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable(onClick = onClear)
                                .padding(horizontal = 16.dp, vertical = 16.dp)
                        )
                        HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))
                    }
                }
                items(filtered) { customer ->
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onSelect(customer) }
                            .padding(horizontal = 16.dp, vertical = 12.dp)
                    ) {
                        Text(
                            text = customerDisplayName(customer),
                            style = MaterialTheme.typography.bodyLarge
                        )
                        if (customer.phone.isNotEmpty()) {
                            Text(
                                text = customer.phone,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                    HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))
                }
                if (allowNewCustomer) item {
                    Text(
                        text = "New customer…",
                        style = MaterialTheme.typography.bodyLarge,
                        color = MaterialTheme.colorScheme.tertiary,
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { addingNew = !addingNew }
                            .padding(horizontal = 16.dp, vertical = 16.dp)
                    )
                    if (addingNew) {
                        Column(modifier = Modifier.padding(horizontal = 16.dp).padding(bottom = 8.dp)) {
                            TimesheetTextField(
                                text = contactName,
                                placeholder = "Contact name",
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    imeAction = ImeAction.Next,
                                    capitalization = KeyboardCapitalization.Words
                                ),
                                onTextChange = { contactName = it; nameError = null }
                            )
                            nameError?.let { FieldError(it) }
                            Spacer(Modifier.height(8.dp))
                            TimesheetTextField(
                                text = companyName,
                                placeholder = "Company name",
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    imeAction = ImeAction.Next,
                                    capitalization = KeyboardCapitalization.Words
                                ),
                                onTextChange = { companyName = it }
                            )
                            Spacer(Modifier.height(8.dp))
                            TimesheetTextField(
                                text = address,
                                placeholder = "Address",
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    imeAction = ImeAction.Next,
                                    capitalization = KeyboardCapitalization.Words
                                ),
                                onTextChange = { address = it }
                            )
                            Spacer(Modifier.height(8.dp))
                            TimesheetTextField(
                                text = phone,
                                placeholder = "Phone",
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Phone,
                                    imeAction = ImeAction.Next
                                ),
                                onTextChange = { phone = it }
                            )
                            Spacer(Modifier.height(8.dp))
                            TimesheetTextField(
                                text = email,
                                placeholder = "Email",
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    keyboardType = KeyboardType.Email,
                                    imeAction = ImeAction.Next
                                ),
                                onTextChange = { email = it }
                            )
                            Spacer(Modifier.height(8.dp))
                            TimesheetTextField(
                                text = paymentTerms,
                                placeholder = "Payment terms",
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(
                                    imeAction = ImeAction.Next,
                                    capitalization = KeyboardCapitalization.Words
                                ),
                                onTextChange = { paymentTerms = it }
                            )
                            Spacer(Modifier.height(8.dp))
                            TimesheetTextField(
                                text = notes,
                                placeholder = "Notes",
                                keyboardOptions = KeyboardOptions(
                                    imeAction = ImeAction.Done,
                                    capitalization = KeyboardCapitalization.Sentences
                                ),
                                keyboardActions = KeyboardActions(onDone = { submitNew() }),
                                onTextChange = { notes = it }
                            )
                            Spacer(Modifier.height(12.dp))
                            Button(
                                onClick = { submitNew() },
                                modifier = Modifier.fillMaxWidth(),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.tertiary,
                                    contentColor = MaterialTheme.colorScheme.onTertiary
                                )
                            ) {
                                Text("Use this customer")
                            }
                        }
                    }
                }
            }
        }
    }
}
